import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { BillingService } from '../billing/billing.service';
import { ReturnsService } from '../../returns/returns.service';
import { ServiceBillingListComponent } from '../service-billing-list/service-billing-list.component';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Router, RouterLink } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'app-billing-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    ReactiveFormsModule,
    ServiceBillingListComponent,
  ],
  templateUrl: './billing-list.component.html',
  styleUrls: ['./billing-list.component.scss'],
})
export class BillingListComponent implements OnInit {
  billingList: any[] = [];
  filteredBillingList: any[] = [];
  search = new FormControl('');
  fromDate: string | null = null;
  toDate: string | null = null;
  selectedConsumableFilter: string = '';
  isLoading = false;
  totalCount: number = 0;
  currentPage: number = 1;
  Math = Math;
  pageSize: number = 10;

  // Summary values
  totalBills = 0;
  totalAmount = 0;
  totalGstAmount = 0;
  totalCessAmount = 0;
  totalDiscountAmount = 0;
  consumableBills = 0;
  // Returns summary (COMPLETED returns; respects the fromDate/toDate filter).
  returnsCount = 0;
  returnsTotal = 0;
  showReportModal = false;
  reportType: 'day' | 'week' | 'month' = 'day';
  startDate: string = '';
  endDate: string = '';
  selectedPreset: string | null = null;

  // Service Billing
  activeTab: 'product' | 'service' = 'product';
  isClinicClient: boolean = false;

  constructor(
    private billingService: BillingService,
    private returnsService: ReturnsService,
    private toastr: ToastrService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.checkClientType();
    this.getBillingList();
    this.loadReturnsSummary();
    this.search.valueChanges.pipe(debounceTime(400)).subscribe(() => {
      this.getBillingList(1);
    });
  }

  /**
   * Returns summary card data. Scoped to the same date window as the bill list
   * when fromDate/toDate are set so the "Returns" figure lines up with sales.
   * Best-effort — a failure just leaves the card at zero, never blocks the page.
   */
  loadReturnsSummary(): void {
    this.returnsService
      .summary({
        startDate: this.fromDate || undefined,
        endDate: this.toDate || undefined,
      })
      .subscribe({
        next: (s) => {
          this.returnsCount = s.count || 0;
          this.returnsTotal = parseFloat(s.totalRefunded) || 0;
        },
        error: () => {
          this.returnsCount = 0;
          this.returnsTotal = 0;
        },
      });
  }

  get totalPages(): number {
    return Math.ceil(this.totalCount / this.pageSize);
  }

  getVisiblePages(): number[] {
    const pages: number[] = [];
    const start = Math.max(2, this.currentPage - 2);
    const end = Math.min(this.totalPages - 1, this.currentPage + 2);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.getBillingList(page, this.pageSize);
  }

  getBillingList(page: number = 1, limit: number = 10) {
    this.currentPage = page;
    this.isLoading = true;
    const searchTerm = this.search.value || '';

    this.billingService.getBillingList(limit, page, searchTerm).subscribe({
      next: (res: any) => {
        if (res && res.data && Array.isArray(res.data)) {
          this.billingList = res.data;
          this.totalCount = res.total || res.data.length;
        } else if (Array.isArray(res)) {
          this.billingList = res;
          this.totalCount = res.length;
        } else {
          this.billingList = [];
          this.totalCount = 0;
          this.toastr.warning('No billing data received', 'Warning');
        }

        this.applyFilters();
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error fetching billing list:', err);
        this.toastr.error('Error fetching billing list', 'Error');
        this.billingList = [];
        this.filteredBillingList = [];
        this.totalCount = 0;
        this.isLoading = false;
      },
    });
  }

  applyFilters() {
    let list = [...this.billingList];

    if (this.selectedConsumableFilter) {
      const isConsumable = this.selectedConsumableFilter === 'consumable';
      list = list.filter((b) => b.isConsumable === isConsumable);
    }

    this.filteredBillingList = list;
    this.calculateSummary();
  }

  onConsumableFilterChange() {
    this.applyFilters();
  }

  onDateRangeChange() {
    this.applyFilters();
    this.loadReturnsSummary();
  }

  clearFilters() {
    this.search.setValue('');
    this.selectedConsumableFilter = '';
    this.fromDate = '';
    this.toDate = '';
    this.applyFilters();
    this.loadReturnsSummary();
  }

  calculateSummary() {
    this.totalBills = this.filteredBillingList.length;
    this.totalAmount = this.filteredBillingList.reduce(
      (sum, b) => sum + (parseFloat(b.grandTotal) || 0),
      0,
    );
    this.totalGstAmount = this.filteredBillingList.reduce(
      (sum, b) => sum + (parseFloat(b.gstAmount) || 0),
      0,
    );
    this.totalCessAmount = this.filteredBillingList.reduce(
      (sum, b) => sum + (parseFloat(b.cessAmount) || 0),
      0,
    );
    this.totalDiscountAmount = this.filteredBillingList.reduce(
      (sum, b) => sum + (parseFloat(b.totalDiscount) || 0),
      0,
    );
    this.consumableBills = this.filteredBillingList.filter(
      (b) => b.isConsumable,
    ).length;
  }

  formatCurrency(value: string | number): string {
    const num =
      typeof value === 'string' ? parseFloat(value || '0') : value || 0;
    return `₹${num.toFixed(2)}`;
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return isNaN(date.getTime())
      ? 'Invalid Date'
      : date.toLocaleDateString('en-IN');
  }

  formatDateTime(dateStr: string): string {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return isNaN(date.getTime())
      ? 'Invalid Date'
      : date.toLocaleString('en-IN');
  }

  // Check if any filters are applied
  get hasFiltersApplied(): boolean {
    return !!(
      this.search.value ||
      this.selectedConsumableFilter ||
      this.fromDate ||
      this.toDate
    );
  }

  viewBilling(id: string) {
    console.log('View billing', id);
    // Add your navigation logic here
  }

  editBilling(id: string) {
    console.log('Edit billing', id);
    // Add your navigation logic here
  }

  printBilling(id: string) {
    console.log('Print billing', id);
    // Add your print logic here
  }

  setRelative(preset: string) {
    this.selectedPreset = preset; // ✅ only one preset active at a time
    this.fromDate = null; // clear manual date when preset selected
    this.toDate = null;
  }

  exportToCSV() {
    console.log('Export billing data to CSV');
    // Implement CSV export logic
    try {
      const csvData = this.filteredBillingList.map((bill) => ({
        'Bill No': bill.billNo || bill.billingNumber,
        'Billing Number': bill.billingNumber,
        'Billing Date': this.formatDate(bill.billingDate),
        'Grand Total': bill.grandTotal,
        'GST Amount': bill.gstAmount,
        'CESS Amount': bill.cessAmount,
        Discount: bill.totalDiscount,
        Type: bill.isConsumable ? 'Consumable' : 'Product',
        'Products Count': bill.products?.length || 0,
      }));

      // You can use a library like ngx-csv or implement custom CSV export
      console.log('CSV Data:', csvData);
    } catch (error) {
      this.toastr.error('Failed to export data');
    }
  }

  refreshData() {
    this.getBillingList();
  }

  // TrackBy function for better performance
  trackByBillingId(index: number, billing: any): string {
    return billing.id || index;
  }

  downloadPDF(id: string) {
    this.billingService.downloadBill(id).subscribe((blob: Blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `billing-${id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    });
  }

  // Utility to extract filename from Content-Disposition header or fallback
  private extractFilename(response: HttpResponse<Blob>, id: string): string {
    const contentDisposition = response.headers.get('content-disposition');
    if (contentDisposition) {
      const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
        contentDisposition,
      );
      if (matches != null && matches[1]) {
        return matches[1].replace(/['"]/g, '');
      }
    }
    return `billing-${id}.pdf`;
  }

  openReportModal() {
    this.showReportModal = true;
  }

  closeReportModal() {
    this.showReportModal = false;
  }

  downloadReport() {
    // Build request payload dynamically
    const payload: any = {};
    if (this.selectedPreset) payload.preset = this.selectedPreset;
    if (this.fromDate) payload.fromDate = this.fromDate;
    if (this.toDate) payload.toDate = this.toDate;

    // If nothing is selected, warn user
    if (!payload.preset && !payload.fromDate && !payload.toDate) {
      this.toastr.warning('Please select a preset or date range');
      return;
    }

    this.isLoading = true;
    this.billingService.exportCsv(payload).subscribe({
      next: (blob: Blob) => {
        // Create file name depending on selection
        let filename = 'billing-report.csv';
        if (payload.preset) {
          filename = `billing-report-${payload.preset}.csv`;
        } else if (payload.fromDate && payload.toDate) {
          filename = `billing-report-${payload.fromDate}-to-${payload.toDate}.csv`;
        }

        // Trigger download
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
        if (payload.preset) {
          this.toastr.success(
            `${payload.preset} report downloaded successfully`,
          );
        } else {
          this.toastr.success(
            `${payload.fromDate} to ${payload.toDate} report downloaded successfully`,
          );
        }

        this.closeReportModal();
        this.isLoading = false;
      },
      error: (err: any) => {
        console.error('Error downloading report:', err);
        this.toastr.error('Failed to download report');
        this.isLoading = false;
      },
    });
  }

  checkClientType() {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        this.isClinicClient = user.clientType === 'Clinic';
      } catch (e) {
        console.error('Error parsing user data', e);
      }
    }
  }

  setActiveTab(tab: 'product' | 'service') {
    this.activeTab = tab;
  }
}
